import { loadImage } from "@hg/recall-frame"

import decoPinImage from "../../assets/imgs/event/deco-pin.png"
import eventCgFrameImage from "../../assets/imgs/event/event-cg-frame.png"
import tipsBgImage from "../../assets/imgs/gallery/tips-bg.png"
import tipsIconImage from "../../assets/imgs/gallery/tips-icon.png"
import shareBgImage from "../../assets/imgs/share/bg.png"
import charNameBgImage from "../../assets/imgs/share/char-name-bg.png"
import demoCharImage from "../../assets/imgs/share/demo-char.png"
import dialogBgImage from "../../assets/imgs/share/dialog-bg.png"
import shareLineImage from "../../assets/imgs/share/line.png"
import qrcodeBgImage from "../../assets/imgs/share/qrcode-bg.png"

const CANVAS_WIDTH = 1920
const CANVAS_HEIGHT = 1080
const DESIGN_WIDTH = 1658
const DESIGN_HEIGHT = 882

const MAIN_WIDTH = 1109
const MAIN_HEIGHT = 696
const SIDE_WIDTH = 333
const SIDE_HEIGHT = 715
const SIDE_GAP = 10
const CONTENT_WIDTH = MAIN_WIDTH + SIDE_GAP + SIDE_WIDTH

const MAIN_X = (DESIGN_WIDTH - CONTENT_WIDTH) / 2
const MAIN_Y = (DESIGN_HEIGHT - MAIN_HEIGHT) / 2
const SIDE_X = MAIN_X + MAIN_WIDTH + SIDE_GAP
const SIDE_Y = (DESIGN_HEIGHT - SIDE_HEIGHT) / 2

const FONT_SANS = '"source-han-sans-japanese", sans-serif'
const FONT_TRAJAN = '"Trajan-Bold", "Times New Roman", serif'
const FONT_NAME = '"source-han-sans-japanese", "STKaiti", serif'

// dialog 内容宽：333 - 27×2 = 279px（设计稿空间）
const DIALOG_CONTENT_WIDTH = 279

type DrawTextOptions = {
    x: number
    y: number
    maxWidth?: number
    maxLines?: number
    lineHeight: number
    font: string
    color: string
    textAlign?: CanvasTextAlign
    letterSpacing?: string
}

export interface DrawShareImageOptions {
    galleryImage: string
    qrCodeUrl: string
    tips: string[]
    appellation: string
    name: string
    dialogText: string
    hideDialog?: boolean
    charImage?: string
}

export interface DrawShareImageResult {
    canvas: HTMLCanvasElement
    base64: string
}

type Rect = {
    x: number
    y: number
    width: number
    height: number
}

type LayoutMetrics = {
    scale: number
    offsetX: number
    offsetY: number
}

const createLayoutMetrics = (): LayoutMetrics => {
    const scale = Math.min(
        CANVAS_WIDTH / DESIGN_WIDTH,
        CANVAS_HEIGHT / DESIGN_HEIGHT,
    )

    return {
        scale,
        offsetX: (CANVAS_WIDTH - DESIGN_WIDTH * scale) / 2,
        offsetY: (CANVAS_HEIGHT - DESIGN_HEIGHT * scale) / 2,
    }
}

const scaleValue = (value: number, metrics: LayoutMetrics) =>
    value * metrics.scale

const scaleRect = (rect: Rect, metrics: LayoutMetrics): Rect => ({
    x: metrics.offsetX + scaleValue(rect.x, metrics),
    y: metrics.offsetY + scaleValue(rect.y, metrics),
    width: scaleValue(rect.width, metrics),
    height: scaleValue(rect.height, metrics),
})

const loadFont = async (font: string, text: string) => {
    if (typeof document === "undefined" || !document.fonts) {
        return
    }

    try {
        if (!document.fonts.check(font, text)) {
            await document.fonts.load(font, text)
        }
    } catch {
        // Ignore font loading failures and render with the current browser state.
    }
}

const createCanvas = () => {
    const canvas = document.createElement("canvas")
    canvas.width = CANVAS_WIDTH
    canvas.height = CANVAS_HEIGHT
    return canvas
}

const drawRectImage = (
    ctx: CanvasRenderingContext2D,
    image: CanvasImageSource,
    rect: Rect,
) => {
    ctx.drawImage(image, rect.x, rect.y, rect.width, rect.height)
}

const drawContainImage = (
    ctx: CanvasRenderingContext2D,
    image: { width: number; height: number } & CanvasImageSource,
    rect: Rect,
) => {
    const sourceAspect = image.width / image.height
    const targetAspect = rect.width / rect.height

    let drawWidth = rect.width
    let drawHeight = rect.height
    let drawX = rect.x
    let drawY = rect.y

    if (sourceAspect > targetAspect) {
        drawHeight = rect.width / sourceAspect
        drawY = rect.y + (rect.height - drawHeight)
    } else {
        drawWidth = rect.height * sourceAspect
        drawX = rect.x + (rect.width - drawWidth) / 2
    }

    ctx.drawImage(image, drawX, drawY, drawWidth, drawHeight)
}

const drawCoverImage = (
    ctx: CanvasRenderingContext2D,
    image: { width: number; height: number } & CanvasImageSource,
    rect: Rect,
) => {
    const sourceAspect = image.width / image.height
    const targetAspect = rect.width / rect.height

    let sourceWidth = image.width
    let sourceHeight = image.height
    let sourceX = 0
    let sourceY = 0

    if (sourceAspect > targetAspect) {
        sourceWidth = image.height * targetAspect
        sourceX = (image.width - sourceWidth) / 2
    } else {
        sourceHeight = image.width / targetAspect
        sourceY = (image.height - sourceHeight) / 2
    }

    ctx.drawImage(
        image,
        sourceX,
        sourceY,
        sourceWidth,
        sourceHeight,
        rect.x,
        rect.y,
        rect.width,
        rect.height,
    )
}

const wrapText = (
    ctx: CanvasRenderingContext2D,
    text: string,
    maxWidth: number,
    maxLines = Number.POSITIVE_INFINITY,
) => {
    const paragraphs = text.split("\n")
    const lines: string[] = []

    for (const paragraph of paragraphs) {
        if (!paragraph) {
            lines.push("")
            continue
        }

        let currentLine = ""

        for (const char of Array.from(paragraph)) {
            const nextLine = currentLine + char
            if (
                currentLine &&
                ctx.measureText(nextLine).width > maxWidth &&
                lines.length + 1 < maxLines
            ) {
                lines.push(currentLine)
                currentLine = char
            } else if (
                currentLine &&
                ctx.measureText(nextLine).width > maxWidth
            ) {
                currentLine = nextLine
            } else {
                currentLine = nextLine
            }
        }

        lines.push(currentLine)
    }

    if (lines.length <= maxLines) {
        return lines
    }

    const trimmedLines = lines.slice(0, maxLines)
    let lastLine = trimmedLines[maxLines - 1] || ""

    while (lastLine && ctx.measureText(`${lastLine}...`).width > maxWidth) {
        lastLine = lastLine.slice(0, -1)
    }

    trimmedLines[maxLines - 1] = `${lastLine}...`
    return trimmedLines
}

/**
 * 根据文本内容计算对话框应使用的字号（21 / 17 / 14 px，设计稿空间）。
 * @param rootFontSize - html 的当前 fontSize（px），默认 16（设计稿基准）。
 *   DOM 侧传入 getFontSize() 以匹配 rem 缩放后的实际渲染字号；
 *   Canvas 侧使用默认值，字号由调用方再经 scaleValue 换算。
 */
export const computeDialogFontSize = (
    text: string,
    rootFontSize = 16,
): number => {
    if (typeof document === "undefined") return 21
    const canvas = document.createElement("canvas")
    const ctx = canvas.getContext("2d")
    if (!ctx) return 21
    // font 和容器宽均经 postcss-pxtorem 转为 rem，随视口等比缩放，
    // 因此字号/容器宽的比值固定，用设计稿值（21px / 279px）测量行数等价于任何分辨率下的结果。
    ctx.font = `500 ${(21 / 16) * rootFontSize}px ${FONT_SANS}`
    ;(ctx as CanvasRenderingContext2D & { letterSpacing?: string }).letterSpacing = "-0.01em"
    const lines = wrapText(ctx, text, DIALOG_CONTENT_WIDTH)
    return lines.length > 4 ? 14 : lines.length > 3 ? 17 : 21
}

const drawTextBlock = (
    ctx: CanvasRenderingContext2D,
    text: string,
    options: DrawTextOptions,
) => {
    const {
        x,
        y,
        maxWidth,
        maxLines,
        lineHeight,
        font,
        color,
        textAlign,
        letterSpacing,
    } = options

    ctx.save()
    ctx.font = font
    if (letterSpacing !== undefined) {
        ;(ctx as CanvasRenderingContext2D & { letterSpacing?: string }).letterSpacing = letterSpacing
    }
    ctx.fillStyle = color
    ctx.textAlign = textAlign || "left"
    ctx.textBaseline = "top"

    const lines =
        maxWidth && Number.isFinite(maxWidth)
            ? wrapText(ctx, text, maxWidth, maxLines)
            : [text]

    lines.forEach((line, index) => {
        ctx.fillText(line, x, y + index * lineHeight)
    })

    ctx.restore()
}

type StrokeTextOptions = {
    text: string
    x: number
    y: number
    font: string
    fillColor: string
    innerColor: string
    innerWidth: number
    outerColor: string
    outerWidth: number
}

const drawStrokeText = (
    ctx: CanvasRenderingContext2D,
    options: StrokeTextOptions,
) => {
    const {
        text,
        x,
        y,
        font,
        fillColor,
        innerColor,
        innerWidth,
        outerColor,
        outerWidth,
    } = options

    ctx.save()
    ctx.font = font
    ctx.textAlign = "center"
    ctx.textBaseline = "top"
    ctx.lineJoin = "round"
    ctx.lineCap = "round"

    ctx.strokeStyle = outerColor
    ctx.lineWidth = (innerWidth + outerWidth) * 2
    ctx.strokeText(text, x, y)

    ctx.strokeStyle = innerColor
    ctx.lineWidth = innerWidth * 2
    ctx.strokeText(text, x, y)

    ctx.fillStyle = fillColor
    ctx.fillText(text, x, y)
    ctx.restore()
}

const loadAssets = async (options: DrawShareImageOptions) => {
    const [
        shareBg,
        shareLine,
        decoPin,
        cgFrame,
        tipsBg,
        tipsIcon,
        charImg,
        dialogBg,
        charNameBg,
        qrcodeBg,
        galleryImage,
        qrCodeImage,
    ] = await Promise.all([
        loadImage(shareBgImage),
        loadImage(shareLineImage),
        loadImage(decoPinImage),
        loadImage(eventCgFrameImage),
        loadImage(tipsBgImage),
        loadImage(tipsIconImage),
        loadImage(options.charImage || demoCharImage),
        loadImage(dialogBgImage),
        loadImage(charNameBgImage),
        loadImage(qrcodeBgImage),
        loadImage(options.galleryImage),
        loadImage(options.qrCodeUrl),
    ])

    return {
        shareBg,
        shareLine,
        decoPin,
        cgFrame,
        tipsBg,
        tipsIcon,
        charImg,
        dialogBg,
        charNameBg,
        qrcodeBg,
        galleryImage,
        qrCodeImage,
    }
}

const drawTips = (
    ctx: CanvasRenderingContext2D,
    metrics: LayoutMetrics,
    assets: Awaited<ReturnType<typeof loadAssets>>,
    tips: string[],
) => {
    const tipsRect = scaleRect(
        {
            x: MAIN_X + (MAIN_WIDTH - 534) / 2,
            y: MAIN_Y + 20 + 606 + 11,
            width: 534,
            height: 59,
        },
        metrics,
    )

    drawRectImage(ctx, assets.tipsBg, tipsRect)

    ctx.save()
    ctx.font = `500 ${Math.round(scaleValue(22, metrics))}px ${FONT_SANS}`
    ctx.textBaseline = "middle"
    ctx.textAlign = "left"

    const iconW = scaleValue(39, metrics)
    const iconH = scaleValue(45, metrics)
    const iconMargin = scaleValue(12, metrics)
    const textGap = scaleValue(6, metrics)

    const textWidths = tips.map((text) => ctx.measureText(text).width)
    const totalTextWidth = textWidths.reduce(
        (sum, w, i) => sum + w + (i < tips.length - 1 ? textGap : 0),
        0,
    )
    const totalContentWidth = iconW + iconMargin + totalTextWidth

    // Center content horizontally, matching CSS flex justify-content: center
    const contentStartX = tipsRect.x + (tipsRect.width - totalContentWidth) / 2
    const textY = tipsRect.y + tipsRect.height / 2

    drawRectImage(ctx, assets.tipsIcon, {
        x: contentStartX,
        y: tipsRect.y + (tipsRect.height - iconH) / 2,
        width: iconW,
        height: iconH,
    })

    let cursorX = contentStartX + iconW + iconMargin
    tips.forEach((text, index) => {
        ctx.fillStyle = index === 0 ? "#ffef85" : "#ffffff"
        ctx.fillText(text, cursorX, textY)
        cursorX += textWidths[index] + (index < tips.length - 1 ? textGap : 0)
    })

    ctx.restore()
}

const drawDialog = (
    ctx: CanvasRenderingContext2D,
    metrics: LayoutMetrics,
    assets: Awaited<ReturnType<typeof loadAssets>>,
    options: DrawShareImageOptions,
) => {
    const charRect = scaleRect(
        {
            x: SIDE_X + SIDE_WIDTH - 166,
            y: SIDE_Y,
            width: 166,
            height: 216,
        },
        metrics,
    )
    const dialogRect = scaleRect(
        {
            x: SIDE_X,
            y: SIDE_Y + 196,
            width: 333,
            height: 180,
        },
        metrics,
    )
    const nameBgRect = scaleRect(
        {
            x: SIDE_X - 4,
            y: SIDE_Y + 182,
            width: 131,
            height: 35,
        },
        metrics,
    )
    const qrBgRect = scaleRect(
        {
            x: SIDE_X + (SIDE_WIDTH - 304) / 2,
            y: SIDE_Y + 411,
            width: 304,
            height: 304,
        },
        metrics,
    )
    const qrRect = scaleRect(
        {
            x: SIDE_X + (SIDE_WIDTH - 268) / 2,
            y: SIDE_Y + 429,
            width: 268,
            height: 268,
        },
        metrics,
    )

    if (!options.hideDialog) {
        drawContainImage(ctx, assets.charImg, charRect)
        drawRectImage(ctx, assets.dialogBg, dialogRect)
        drawRectImage(ctx, assets.charNameBg, nameBgRect)

        drawStrokeText(ctx, {
            text: options.appellation,
            x: nameBgRect.x + nameBgRect.width / 2,
            y: nameBgRect.y - scaleValue(12, metrics),
            font: `${Math.round(scaleValue(11, metrics))}px ${FONT_TRAJAN}`,
            fillColor: "#443122",
            innerColor: "#ffffff",
            innerWidth: scaleValue(2, metrics),
            outerColor: "#a9885b",
            outerWidth: scaleValue(2, metrics),
        })
        drawStrokeText(ctx, {
            text: options.name,
            x: nameBgRect.x + nameBgRect.width / 2,
            y: nameBgRect.y - scaleValue(2, metrics),
            font: `${Math.round(scaleValue(28, metrics))}px ${FONT_NAME}`,
            fillColor: "#443122",
            innerColor: "#ffffff",
            innerWidth: scaleValue(3, metrics),
            outerColor: "#f6d1aa",
            outerWidth: scaleValue(3, metrics),
        })

        const dialogMaxWidth = dialogRect.width - scaleValue(54, metrics)
        const dialogFontSize = scaleValue(
            computeDialogFontSize(options.dialogText),
            metrics,
        )

        drawTextBlock(ctx, options.dialogText, {
            x: dialogRect.x + scaleValue(27, metrics),
            y: dialogRect.y + scaleValue(35, metrics),
            maxWidth: dialogMaxWidth,
            maxLines: 4,
            lineHeight: scaleValue(33, metrics),
            font: `500 ${Math.round(dialogFontSize)}px ${FONT_SANS}`,
            color: "#443122",
            letterSpacing: "-0.01em",
        })
    }

    drawRectImage(ctx, assets.qrcodeBg, qrBgRect)
    drawRectImage(ctx, assets.qrCodeImage, qrRect)
}

export const drawShareImage = async (
    options: DrawShareImageOptions,
): Promise<DrawShareImageResult> => {
    const canvas = createCanvas()
    const ctx = canvas.getContext("2d")

    if (!ctx) {
        throw new Error("2d canvas context is not available")
    }

    const metrics = createLayoutMetrics()
    const assets = await loadAssets(options)

    await Promise.all([
        loadFont(
            `${Math.round(scaleValue(11, metrics))}px ${FONT_TRAJAN}`,
            options.appellation,
        ),
        loadFont(
            `${Math.round(scaleValue(28, metrics))}px ${FONT_NAME}`,
            options.name,
        ),
    ])

    drawRectImage(
        ctx,
        assets.shareBg,
        scaleRect(
            {
                x: 0,
                y: 0,
                width: DESIGN_WIDTH,
                height: DESIGN_HEIGHT,
            },
            metrics,
        ),
    )
    drawRectImage(
        ctx,
        assets.shareLine,
        scaleRect(
            {
                x: 229,
                y: 724,
                width: 965,
                height: 66,
            },
            metrics,
        ),
    )

    const cgFrameRect = scaleRect(
        {
            x: MAIN_X,
            y: MAIN_Y + 20,
            width: 1109,
            height: 606,
        },
        metrics,
    )
    const cgImageRect = scaleRect(
        {
            x: MAIN_X + 43,
            y: MAIN_Y + 20 + 28,
            width: 1023,
            height: 553,
        },
        metrics,
    )

    drawRectImage(ctx, assets.cgFrame, cgFrameRect)
    drawCoverImage(ctx, assets.galleryImage, cgImageRect)
    drawRectImage(
        ctx,
        assets.decoPin,
        scaleRect(
            {
                x: 186,
                y: 19,
                width: 71,
                height: 138,
            },
            metrics,
        ),
    )

    drawTips(ctx, metrics, assets, options.tips)
    drawDialog(ctx, metrics, assets, options)

    return {
        canvas,
        base64: canvas.toDataURL("image/png"),
    }
}
